/**
 * TOSS Web Application — Client Portal company & profile API
 *
 * The reads and the one write behind the portal's profile screen and header:
 *   - `GET /api/client/company` — the customer company card (release Р1);
 *   - `GET /api/client/users/me` — "who am I" (shipped by Phase 0, extended by Р4 with
 *     `availableCompanies`);
 *   - `POST /api/client/users/active-company` — switch which company the session acts
 *     for (release Р4, decision Р20).
 *
 * Contract: vault/specs/client-portal/prep-work/r1-api-contract.md §2 (company/profile
 * reads); r4-company-switch-rewrites-the-session-not-the-token.md §3.1 (the switch).
 *
 * The company card is a hand-built DTO, not `CustomerFullDto`: the internal one carries
 * `materialsMarkupPercent`, `overrideMileageClientRate`, `paymentTerm`, `applicationPrefix` and
 * `responsibleUserId` — commercial terms the customer must never read back.
 */

import apiClient from '@/api/client';
import { CLIENT_API_BASE } from './clientPortal.types';
import type { ClientCompanyDto } from './clientPortal.types';
import type { UserRole, UserStatus } from '@/types/enums';

// ============================================================================
// Endpoints
// ============================================================================

const ENDPOINTS = {
  COMPANY: `${CLIENT_API_BASE}/company`,
  ME: `${CLIENT_API_BASE}/users/me`,
  ACTIVE_COMPANY: `${CLIENT_API_BASE}/users/active-company`,
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * One legal entity the signed-in account may act for — mirrors
 * `src/Toss.Api/Features/Users/Client/ClientCompanyRefDto.cs` field for field.
 *
 * A picker option, not a company card. It carries an id, a name and a COUNT, and the count
 * is the only cross-company disclosure in the whole portal — decision B7 amendment 1
 * (`r4-contract-decisions.md`): notification recipients now resolve through company
 * membership, so an employee of А and Б receives А's approval while acting as Б, and every
 * read path then hides it from them (the feed is scoped to the active company, marking it
 * read answers 404, the live push is suppressed). The number is what stops that from being
 * a notification nobody can discover.
 *
 * It stays safe by staying a number: no title, no application number, no sum, no id, no
 * deep link. Anything richer added here would be served for a company the session is NOT
 * currently acting for, which is exactly what the scoped reads are withholding until the
 * customer switches.
 */
export interface ClientCompanyRefDto {
  /** Company id — the value `POST /api/client/users/active-company` takes. */
  id: string;
  /** Company name, as the switcher renders it. */
  name: string;
  /**
   * Unread notifications the account has under that company — what the bell badge reads
   * after switching to it. Present for every entry, the active company included (where it
   * equals the badge the header already shows).
   */
  unreadCount: number;
}

/**
 * Response of `GET /api/client/users/me` and of
 * `POST /api/client/users/active-company` — mirrors
 * `src/Toss.Api/Features/Users/Client/ClientProfileDto.cs` field for field.
 *
 * Currently the same base shape as `ClientAuthUserDto`, but they are separate records on the
 * backend and are typed separately here so one can grow without silently changing the other.
 *
 * This is the call site `authStore.initialize()` uses to restore a session on reload, and it
 * casts the payload to `User` with no runtime validation — which is why `User` carries optional
 * `customerId` / `customerName` (see `src/types/user.types.ts`). Note that `isMentor`,
 * `isRulesAccepted`, `isPushEnabled` and `createdAt` are simply absent from this payload: a
 * restored client session has them as `undefined` even though the `User` type claims otherwise.
 */
export interface ClientProfileDto {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  /** Always `UserRole.Client` — the frontend derives the session audience from it. */
  role: UserRole;
  status: UserStatus;
  customerId: string;
  customerName: string;
  /**
   * Every company this account may act for, by name — ordered by name, always contains
   * `customerId` (release Р4). A single entry is the ordinary case: the header switcher
   * renders nothing at all for it, exactly as the account saw before this field existed.
   */
  availableCompanies: ClientCompanyRefDto[];
  /**
   * Whether this account is the ACTIVE company's portal owner — the customer's own employee
   * allowed to choose which colleagues take part in the company chat. `false` for everybody else,
   * and for every account of a company ТОСС has not named an owner for.
   *
   * Per active company, not per account: an owner of one legal entity is an ordinary employee of
   * the other, so the flag flips with the header switcher.
   */
  isPortalOwner: boolean;
  /**
   * Whether the «+» that starts a new chat belongs on this person's screen.
   *
   * Two conditions folded into one answer by the server: they own the cabinet, AND the server is
   * accepting chat creation at all. Folded on purpose — the cabinet's rule is that an action it
   * cannot take is ABSENT rather than disabled, and deciding that must not require the frontend to
   * know what a feature flag is.
   */
  canCreateChats: boolean;
  /** Должность, as the customer typed it. Editable by them through `PUT /api/client/users/me`. */
  position?: string | null;
  /**
   * The «Push-уведомления» preference. Stored today, honoured when the portal starts delivering
   * pushes — decision Р36 keeps them off for customers, so the toggle is a preference, not a promise.
   */
  isPushEnabled?: boolean;
  /**
   * The caller's role inside their OWN company. Per ACTIVE company, exactly like
   * {@link ClientProfileDto.isPortalOwner}: an administrator of one legal entity is an ordinary
   * employee of the other, so both flip with the header switcher.
   */
  clientRole?: string;
  /** Its Russian name, shipped by the server so one spelling exists. */
  roleName?: string;
  /**
   * Everything the caller may do in the active company, as `client.*` keys — read through
   * `useClientCapabilities`. What the UI hides by; never what protects anything.
   */
  permissions?: string[];
}

// ============================================================================
// Reads
// ============================================================================

/**
 * The signed-in user's own company card, with its contact people. Read-only in Р1.
 *
 * @throws 403 `CLIENT_COMPANY_NOT_BOUND` for an account with no company binding.
 */
export async function getClientCompany(): Promise<ClientCompanyDto> {
  const response = await apiClient.get<ClientCompanyDto>(ENDPOINTS.COMPANY);
  return response.data;
}

/** The signed-in portal account. */
export async function getClientProfile(): Promise<ClientProfileDto> {
  const response = await apiClient.get<ClientProfileDto>(ENDPOINTS.ME);
  return response.data;
}

/** What a customer may change about themselves. Email is absent and never will be — it is the login. */
export interface UpdateClientProfileRequest {
  fullName: string;
  phoneNumber: string | null;
  position: string | null;
  isPushEnabled: boolean;
}

/**
 * `PUT /api/client/users/me` — the portal's first profile write.
 *
 * Until the roles release this endpoint did not exist, and the phone screen offered «Написать
 * менеджеру» in its place. Answers the full profile, so the caller can drop it straight into the
 * cache rather than refetching.
 */
export async function updateClientProfile(
  data: UpdateClientProfileRequest
): Promise<ClientProfileDto> {
  const response = await apiClient.put<ClientProfileDto>(ENDPOINTS.ME, data);
  return response.data;
}

// ============================================================================
// Writes
// ============================================================================

/**
 * Points the session at another of the caller's own companies (release Р4, decision Р20).
 *
 * Idempotent — the already-active company answers 200 and writes nothing server-side, so a
 * double-click on the dropdown is not an error. Answers **404**, not 403, for a company the
 * account is not a member of: confirming that a company exists is the leak here.
 *
 * Tokens are never reissued: no token carries a company, `Users.CustomerId` alone is the
 * switch, so the caller applying this response (see `applyCompanySwitch` in
 * `hooks/api/clientPortal/clientCompanySwitch.ts`) does not touch `TokenStorage`.
 *
 * @throws 404 when `customerId` is not one of the caller's `UserCustomers` memberships.
 */
export async function switchClientActiveCompany(customerId: string): Promise<ClientProfileDto> {
  const response = await apiClient.post<ClientProfileDto>(ENDPOINTS.ACTIVE_COMPANY, {
    customerId,
  });
  return response.data;
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Client company / profile API namespace.
 *
 * @example
 * ```ts
 * import { clientCompanyApi } from '@/api/clientPortal/clientCompany.api';
 *
 * const company = await clientCompanyApi.getCompany();
 * ```
 */
export const clientCompanyApi = {
  getCompany: getClientCompany,
  getProfile: getClientProfile,
  updateProfile: updateClientProfile,
  switchActiveCompany: switchClientActiveCompany,
} as const;
